import { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { BroadcasterModule } from './broadcasterModule.jsx';
import { InfoModule } from './infoModule.jsx';
import { TutorialModule } from './tutorialModule.jsx';
import { GlossaryModule } from './glossaryModule.jsx';

export const Whiteboard = ({ allRulesData, broadcasters }) => {
    const containerRef = useRef(null);
    const zoomableContainerRef = useRef(null);
    const [nodesData, setNodesData] = useState([]);

    const nodeElementRefs = useRef({});

    const [currentZoomTransform, setCurrentZoomTransform] = useState(d3.zoomIdentity);

    // --- New: Add a unique ID counter for new modules ---
    const nextNodeId = useRef(0);

    // --- New: Function to add a new module ---
    const addBroadcasterModule = useCallback((nodeType) => {
        if (!containerRef.current) return;

        const width = containerRef.current.offsetWidth;
        const height = containerRef.current.offsetHeight;

        // Calculate a random position within the center area
        const centerX = width / 2;
        const centerY = height / 2;
        const spread = 150; // Adjust this value to control how spread out new modules are
        const randomX = centerX + (Math.random() - 0.5) * spread;
        const randomY = centerY + (Math.random() - 0.5) * spread;

        const newNode = {
            id: `module-${nextNodeId.current++}`,
            nodeType: nodeType,
            originalData: {}, // You might want to pass actual data here if needed
            x: randomX,
            y: randomY,
            fx: null,
            fy: null,
        };

        setNodesData(prevNodes => {
            const newNodes = [...prevNodes, newNode];
            return newNodes;
        });
    }, []);

    // --- New: Effect to add one module on initial load ---
    useEffect(() => {
        if (nodesData.length === 0 && containerRef.current) {
            // Ensure container dimensions are available
            const width = containerRef.current.offsetWidth;
            const height = containerRef.current.offsetHeight;

            const initialNode = {
                id: `module-${nextNodeId.current++}`,
                nodeType: "broadcaster",
                originalData: {},
                x: width / 2, // Center horizontally
                y: height / 1.5, // Center vertically
                fx: null, // Start unfixed
                fy: null, // Start unfixed
            };
            const tutorialNode = {
                id: `module-${nextNodeId.current++}`,
                nodeType: "tutorial",
                originalData: {},
                x: width / 2, // Center horizontally
                y: height / 3, // Center vertically
                fx: null, // Start unfixed
                fy: null, // Start unfixed
            };
            setNodesData([tutorialNode,initialNode]);
        }
    }, [containerRef, nodesData.length]); // Depend on containerRef to ensure it's mounted and measured

    useEffect(() => {
        if (nodesData.length === 0 || !containerRef.current) return;

        nodesData.forEach(node => {
            const el = nodeElementRefs.current[node.id];
            if (el && node.x !== undefined && node.y !== undefined) {
                el.style.left = `${node.x}px`;
                el.style.top = `${node.y}px`;
            }
        });
    }, [nodesData]);

    // var start_x, start_y;
    // Effect to apply D3 drag behavior to each node's DOM element
    useEffect(() => {
        var start_x, start_y;
        const dragBehavior = d3.drag()
            .on('start', (event, d) => {
                start_x = event.x;
                start_y = event.y;
            })
            .on('drag', (event, d) => {
                if (start_x !== undefined && start_y !== undefined) {
                    const newX = start_x + ((event.x - start_x) / currentZoomTransform.k);
                    const newY = start_y + ((event.y - start_y) / currentZoomTransform.k);
                    d.fx = newX;
                    d.fy = newY;

                    setNodesData(prevNodes =>
                        prevNodes.map(node =>
                            node.id === d.id
                                ? { ...node, x: newX, y: newY } // Update the dragged node's position
                                : node // Keep other nodes as they are
                        )
                    );
                }
            })
            .on('end', (event, d) => {
                d.fx = null;
                d.fy = null;
            });

        nodesData.forEach(node => {
            const el = nodeElementRefs.current[node.id];
            if (el) {
                d3.select(el)
                    .datum(node)
                    .call(dragBehavior);
            }
        });

        const nodeElements = nodeElementRefs.current

        return () => {
            nodesData.forEach(node => {
                const el = nodeElements[node.id];
                if (el) {
                    d3.select(el).on('.drag', null);
                }
            });
        };
    }, [nodesData, currentZoomTransform]);

    // Effect for D3 Zoom behavior
    useEffect(() => {
        if (!containerRef.current || !zoomableContainerRef.current) return;

        const svg = d3.select(containerRef.current);

        const width = containerRef.current.offsetWidth;
        const height = containerRef.current.offsetHeight;

        const zoomBehavior = d3.zoom()
            .scaleExtent([0.5, 7])
            .on('zoom', (event) => {
                d3.select(zoomableContainerRef.current).style('transform', `translate(${event.transform.x}px, ${event.transform.y}px) scale(${event.transform.k})`);
                setCurrentZoomTransform(event.transform);
            });

        svg.call(zoomBehavior);

        const isMobile = window.innerWidth <= 768;
        let initialTransform = d3.zoomIdentity;

        if (isMobile) {
            const k = 0.6; // Set your desired initial zoom level (e.g., 0.8 for 80%)
            
            // Calculate translation to keep the zoom centered
            const tx = (width / 2) * (1 - k);
            const ty = (height / 2) * (1 - k);
            
            initialTransform = d3.zoomIdentity.translate(tx, ty).scale(k);
        }

        // Initial transform reset (optional, to ensure it starts at identity)
        svg.call(zoomBehavior.transform, initialTransform).on("dblclick.zoom", null);

        return () => {
            svg.on('.zoom', null);
        };
    }, []);

    // Function to programmatically zoom
    const handleZoom = useCallback((direction) => {
        if (!containerRef.current) return;
        const svg = d3.select(containerRef.current);
        const zoomBehavior = d3.zoom()
            .scaleExtent([0.1, 50])
            .on('zoom', (event) => {
                d3.select(zoomableContainerRef.current).style('transform', `translate(${event.transform.x}px, ${event.transform.y}px) scale(${event.transform.k})`);
                setCurrentZoomTransform(event.transform);
            });

        if (direction === 'reset') {
            svg.transition().duration(250).call(zoomBehavior.transform, d3.zoomIdentity);
        }
    }, []);

    // --- New: Function to remove a module ---
    const removeBroadcasterModule = useCallback((idToRemove) => {
        setNodesData(prevNodes => {
            const newNodes = prevNodes.filter(node => node.id !== idToRemove);
            return newNodes;
        });
        // Clean up the ref entry as well
        if (nodeElementRefs.current[idToRemove]) {
            nodeElementRefs.current[idToRemove] = null;
        }
    }, []);

    return (
        <div
            ref={containerRef}
            id="whiteboard-container"
        >
            
            <div id="whiteboard-info">
                <a id="logo-title" href="https://arvester.eu/" target="_blank" rel="noreferrer">Arvester</a>
                <div id="whiteboard-title-info">
                    <h1 id="whiteboard-title">Infobligations</h1>
                    <span id="whiteboard-title-beta">BETA</span>
                </div>
            </div>
            <button onClick={() => addBroadcasterModule("broadcaster")} id="add-module-button">Ajouter un module</button>
            <div id="whiteboard-options">
                <button id="whiteboard-info-button" onClick={() => addBroadcasterModule("info")}>À propos</button>
                <button id="whiteboard-info-button" onClick={() => addBroadcasterModule("tutorial")}>Mode d'emploi</button>
                <button id="whiteboard-info-button" onClick={() => addBroadcasterModule("glossary")}>Glossaire</button>
            </div>
            <div id="zoom-functions-container">
                <button onClick={() => handleZoom('reset')} id="reset-zoom-button">
                    <span id="zoom-tracker">{currentZoomTransform.k.toFixed(2)}x</span>
                </button>
            </div>

            {/* This div will be transformed by D3 zoom */}
            <div
                ref={zoomableContainerRef}
                style={{ width: '100%', height: '100%', position: 'absolute', transformOrigin: '0 0' }}
            >
                {nodesData.map(node => (
                    <div
                        key={node.id}
                        ref={el => (nodeElementRefs.current[node.id] = el)}
                        className="draggable-node"
                        style={{
                            position: 'absolute',
                            left: node.x !== undefined ? node.x : 0,
                            top: node.y !== undefined ? node.y : 0,
                            transform: 'translate(-50%, -50%)', // Center the div on its (x,y) coordinates
                            cursor: 'grab',
                            zIndex: 10,
                            border: 'none',
                            boxShadow: '2px 2px 5px rgba(0,0,0,0.2)',
                            padding: '0px',
                            borderRadius: '2px'
                        }}
                    >
                        <button className="module-delete-button" onClick={() => removeBroadcasterModule(node.id)}>&#215;</button>
                        { (node.nodeType === "broadcaster" &&
                                <BroadcasterModule
                                    allRulesData={allRulesData}
                                    broadcasters={broadcasters}
                                    zoomScale={currentZoomTransform.k}
                                />)
                            || (node.nodeType === "info" &&
                                <InfoModule zoomScale={currentZoomTransform.k}/>)
                            || (node.nodeType === "tutorial" &&
                                <TutorialModule zoomScale={currentZoomTransform.k}/>)
                            || (node.nodeType === "glossary" &&
                                <GlossaryModule zoomScale={currentZoomTransform.k}/>)
                            || null
                        }
                    </div>
                ))}
            </div>
        </div>
    );
};